import React, { useState, useEffect } from 'react';
import { useSearchParams } from '@mentimeter/next-navigation';
import { useTranslations } from '@mentimeter/i18n';
import type { NormalizedTemplateCategoryT } from 'shared/cms/templateCategories/normalizeTemplateCategory';
import { FEATURED_CATEGORIES } from 'src/template-constants';
import { Box } from '@mentimeter/ragnar-ui/box';
import NavigationItem from './NavigationItem';

interface Props {
  categories: NormalizedTemplateCategoryT[];
  onItemClick?: () => void;
}
const NavigationContent = ({ categories, onItemClick }: Props) => {
  const t = useTranslations('common');
  const searchParams = useSearchParams();
  const categorySlug = searchParams?.get('category');
  const subcategorySlug = searchParams?.get('subcategory');

  const currentCategory = categories.find((c) => c.slug === categorySlug);
  const initialExpanded = currentCategory?.menuText;

  const [expandedCategories, setExpandedCategories] = useState<string[]>(
    initialExpanded ? [initialExpanded] : [],
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    initialExpanded && setExpandedCategories([initialExpanded]);
  }, [initialExpanded]);

  const toggleExpandCategory = (name: string) => {
    setExpandedCategories((expandedList) =>
      expandedList.includes(name)
        ? expandedList.filter((item) => item !== name)
        : [...expandedList, name],
    );
  };

  return (
    <Box
      as="nav"
      width="100%"
      bg="bg"
      overflow="hidden"
      pr={['space8', null, 'space0']}
      pl={['space8', null, 'space0']}
      pt={['space4', null, 'space0']}
    >
      <NavigationItem
        name={t('templates.explore')}
        href="/templates"
        active={!categorySlug}
        aria-expanded={false} // Not expandable, thus always false
      />
      {categories.map((category) => {
        if (category.id === FEATURED_CATEGORIES) return null;
        const isExpanded = expandedCategories.includes(category.menuText);
        return (
          <NavigationItem
            isExpanded={isExpanded}
            key={category.slug}
            name={category.menuText}
            href={`/templates/${category.slug}`}
            active={categorySlug === category.slug}
            onClickChevron={toggleExpandCategory}
            aria-expanded={isExpanded}
            subitems={category.subcategories.map((subcategory) => ({
              name: subcategory.menuText,
              href: `/templates/${subcategory.parentCategorySlug}/${subcategory.slug}`,
              active: subcategorySlug === subcategory.slug,
              onClick: onItemClick,
            }))}
          />
        );
      })}
    </Box>
  );
};

export default NavigationContent;
